import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Search, Tag, Package, Download, Star, ShoppingCart, Plus, Minus, X, Truck, Calendar, Check, Gift, ChevronRight, Filter, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import ShopComingSoon from '../components/ShopComingSoon';
import { resolveProductImage, productHasImage } from '../lib/productImages';
import ProductImagePlaceholder from '../components/ProductImagePlaceholder';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Feature flag - set to true to re-enable Stripe checkout later
const ENABLE_STRIPE_CHECKOUT = false;

// Safari-compatible external link opener
const openExternalLink = (url) => {
  if (!url) return;
  
  // Create a temporary anchor element for better Safari compatibility
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  
  // Append to body, click, and remove
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Category configuration with icons and subcategories
const SHOP_CATEGORIES = {
  all: { name: 'All Products', icon: ShoppingBag, description: 'Browse our entire range' },
  rods: { 
    name: 'Rods', 
    icon: '🎣',
    description: 'Carp rods for every situation',
    subcategories: ['Distance Rods', 'Stalking Rods', 'Spod Rods', 'Marker Rods']
  },
  reels: { 
    name: 'Reels', 
    icon: '⚙️',
    description: 'Big pit and specialist reels',
    subcategories: ['Big Pit Reels', 'Compact Reels', 'Spod Reels']
  },
  tackle: { 
    name: 'Tackle', 
    icon: '🪝',
    description: 'Terminal tackle and end gear',
    subcategories: ['Hooks', 'Leaders', 'Leads', 'Swivels', 'Rig Materials']
  },
  bait: { 
    name: 'Bait', 
    icon: '🟤',
    description: 'Boilies, particles and attractants',
    subcategories: ['Boilies', 'Pop-ups', 'Particles', 'Liquids & Glugs', 'Pellets']
  },
  clothing: { 
    name: 'Clothing', 
    icon: '👕',
    description: 'Bankside apparel',
    subcategories: ['Jackets', 'Hoodies', 'T-Shirts', 'Trousers', 'Footwear', 'Accessories']
  },
  accessories: { 
    name: 'Accessories', 
    icon: '🎒',
    description: 'Bivvies, bedchairs and more',
    subcategories: ['Bivvies & Shelters', 'Bedchairs', 'Sleeping Bags', 'Bite Alarms', 'Nets & Mats', 'Cooking', 'Luggage']
  },
  guides: { 
    name: 'Digital Guides', 
    icon: '📚',
    description: 'eBooks and video courses',
    subcategories: ['Video Courses', 'eBooks', 'Lake Guides']
  }
};

const Shop = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [sortBy, setSortBy] = useState('featured');
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchFilters();
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);

  // Cart persistence - kept for future re-enabling of Stripe checkout
  useEffect(() => {
    if (ENABLE_STRIPE_CHECKOUT) {
      localStorage.setItem('cart', JSON.stringify(cart));
    }
  }, [cart]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const [brandsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/products/brands/list`),
        axios.get(`${API}/products/categories/list`)
      ]);
      setBrands(brandsRes.data.brands || []);
      setCategories(categoriesRes.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch filters:', error);
    }
  };

  // Open affiliate link - Safari compatible
  const openAffiliateLink = (product) => {
    const url = product.affiliate_url || `https://www.anglingdirect.co.uk/search?q=${encodeURIComponent(product.product_name)}`;
    openExternalLink(url);
  };

  // Cart functions - kept for future re-enabling of Stripe checkout
  const addToCart = (product) => {
    // Server-side and client-side block any product flagged as non-purchasable
    // (e.g. Bait while partnerships are still pending). Honour the affiliate-
    // link fallback if it's wired up for that product, otherwise show the
    // friendly "Coming Soon" toast.
    if (product?.is_purchasable === false) {
      if (product.affiliate_url) {
        openAffiliateLink(product);
        return;
      }
      toast.message(product.purchase_disabled_reason || 'Coming soon — not yet purchasable.', { duration: 5000 });
      return;
    }
    if (!ENABLE_STRIPE_CHECKOUT) {
      openAffiliateLink(product);
      return;
    }
    setCart(prev => {
      const existing = prev.find(item => item.product_id === product.product_id);
      if (existing) {
        return prev.map(item =>
          item.product_id === product.product_id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, {
        product_id: product.product_id,
        product_name: product.product_name,
        brand_name: product.brand_name,
        sku: product.sku,
        category: product.category,
        retail_price: product.retail_price,
        quantity: 1,
        image_url: product.image_url,
        stock_status: product.stock_status,
        is_purchasable: product.is_purchasable,
        purchase_disabled_reason: product.purchase_disabled_reason,
      }];
    });
    toast.success(`${product.product_name} added to cart`);
  };

  const updateCartQuantity = (productId, delta) => {
    setCart(prev => {
      const updated = prev.map(item => {
        if (item.product_id === productId) {
          const newQty = item.quantity + delta;
          return newQty > 0 ? { ...item, quantity: newQty } : null;
        }
        return item;
      }).filter(Boolean);
      return updated;
    });
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.product_id !== productId));
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    // Belt-and-braces: refuse to navigate to checkout if any item in the cart
    // is still flagged as non-purchasable. The Checkout page + backend also
    // gate this independently — this is the user-friendliest first stop.
    const blocked = cart.filter(item =>
      item.is_purchasable === false || item.category === 'Bait'
    );
    if (blocked.length > 0) {
      toast.error(
        `Remove ${blocked.length === 1 ? 'this item' : 'these items'} first: ` +
        blocked.map(b => b.product_name).join(', '),
        { duration: 7000 },
      );
      return;
    }
    navigate('/checkout', { state: { cart } });
  };

  const handleBaitSubscription = async (boxType) => {
    try {
      const response = await axios.post(`${API}/subscriptions/bait-box`, {
        box_type: boxType,
        origin_url: window.location.origin
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe');
    }
  };

  const handleBundlePurchase = async (bundleType) => {
    try {
      const response = await axios.post(`${API}/subscriptions/bait-bundle/checkout?bundle_type=${bundleType}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to checkout bundle');
    }
  };

  // Filter and sort products
  const getFilteredProducts = () => {
    let filtered = products;

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category?.toLowerCase() === selectedCategory.toLowerCase());
    }

    // Brand filter
    if (selectedBrand !== 'all') {
      filtered = filtered.filter(p => p.brand_name === selectedBrand);
    }

    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(p => 
        p.product_name?.toLowerCase().includes(searchLower) ||
        p.description?.toLowerCase().includes(searchLower) ||
        p.category?.toLowerCase().includes(searchLower) ||
        p.brand_name?.toLowerCase().includes(searchLower) ||
        p.sku?.toLowerCase().includes(searchLower) ||
        p.subcategory?.toLowerCase().includes(searchLower)
      );
    }

    // Sort
    switch (sortBy) {
      case 'price-low':
        filtered = [...filtered].sort((a, b) => (a.retail_price || 0) - (b.retail_price || 0));
        break;
      case 'price-high':
        filtered = [...filtered].sort((a, b) => b.price - a.price);
        break;
      case 'name':
        filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'featured':
      default:
        filtered = [...filtered].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    }

    return filtered;
  };

  const filteredProducts = getFilteredProducts();

  // Get product counts per category
  const getCategoryCount = (cat) => {
    if (cat === 'all') return products.length;
    return products.filter(p => p.category?.toLowerCase() === cat.toLowerCase()).length;
  };

  // Calculate cart total
  const getCartTotal = () => {
    return cart.reduce((sum, item) => sum + (item.retail_price * item.quantity), 0);
  };

  const CategorySidebar = ({ mobile = false }) => (
    <div className={`${mobile ? '' : 'hidden lg:block w-64 flex-shrink-0'}`}>
      <div className={`${mobile ? '' : 'sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2'}`} style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(200,178,115,0.3) transparent' }}>
        <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wider text-white mb-4">
          Categories
        </h3>
        <nav className="space-y-1">
          {Object.entries(SHOP_CATEGORIES).map(([key, cat]) => {
            const count = getCategoryCount(key === 'all' ? 'all' : cat.name);
            const isActive = selectedCategory === key;
            
            return (
              <button
                key={key}
                onClick={() => {
                  setSelectedCategory(key);
                  if (mobile) setMobileFiltersOpen(false);
                }}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all cursor-pointer ${
                  isActive 
                    ? 'bg-[#c8b273] text-black' 
                    : 'text-[#8a948a] hover:bg-[#141714] hover:text-white'
                }`}
                data-testid={`category-${key}`}
              >
                <span className="flex items-center gap-3">
                  <span className="text-lg">{typeof cat.icon === 'string' ? cat.icon : '📦'}</span>
                  <span className="font-medium">{cat.name}</span>
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  isActive ? 'bg-black/20' : 'bg-[#141714]'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </nav>

        {/* Brand Filter */}
        {brands.length > 0 && (
          <div className="mt-6">
            <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wider text-white mb-4">
              Brands
            </h3>
            <nav className="space-y-1 max-h-64 overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(200,178,115,0.3) transparent' }}>
              <button
                onClick={() => setSelectedBrand('all')}
                className={`w-full text-left px-3 py-2 rounded-lg transition-all cursor-pointer ${
                  selectedBrand === 'all' 
                    ? 'bg-[#c8b273] text-black' 
                    : 'text-[#8a948a] hover:bg-[#141714] hover:text-white'
                }`}
                data-testid="brand-all"
              >
                All Brands
              </button>
              {brands.map(brand => (
                <button
                  key={brand}
                  onClick={() => setSelectedBrand(brand)}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-all cursor-pointer ${
                    selectedBrand === brand 
                      ? 'bg-[#c8b273] text-black' 
                      : 'text-[#8a948a] hover:bg-[#141714] hover:text-white'
                  }`}
                  data-testid={`brand-${brand.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {brand}
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* Bait Subscription Promo — internal admin preview only.
            Hidden from anglers until the bait fulfilment partnership goes live. */}
        {user?.is_admin && (
        <div className="mt-8 p-4 bg-gradient-to-br from-[#c8b273]/20 to-transparent rounded-lg border border-[#c8b273]/30" data-testid="bait-promo-admin">
          <div className="flex items-center gap-2 mb-2">
            <Gift className="w-7 h-7 text-[#c8b273]" />
            <span className="text-[10px] uppercase tracking-widest text-[#c8b273]/80 border border-[#c8b273]/40 rounded px-1.5 py-0.5">
              Admin Preview
            </span>
          </div>
          <h4 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-white mb-2">
            Bait Subscriptions
          </h4>
          <p className="text-[#8a948a] text-sm mb-3">
            Internal preview — Starter / Angler / Specimen boxes (1–3kg boilies)
          </p>
          <Button 
            className="btn-gold w-full text-sm"
            onClick={() => document.getElementById('bait-sub-section')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Open Preview
          </Button>
        </div>
        )}
      </div>
    </div>
  );

  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = getCartTotal();

  // ============================================================
  // SOFT LAUNCH: Public-facing shop is in coming-soon mode while
  // partnerships are being secured. Admins retain the full live
  // shop + bait preview behind the same URL for internal QA.
  // Flip the gate by changing this condition or wiring it to an
  // env flag once the marketplace is ready to go public.
  // ============================================================
  if (!user?.is_admin) {
    return (
      <div className="min-h-screen" data-testid="shop-page">
        {/* Hero */}
        <div
          className="relative h-48 md:h-64 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1532724414795-8a873d116fab?w=1200&auto=format&fit=crop&q=80')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-[#050605] via-[#050605]/75 to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(200,178,115,0.12),transparent_60%)] pointer-events-none" />
          <div className="relative z-10 h-full flex items-end pb-6 md:pb-8 px-4 md:px-8">
            <div className="max-w-7xl mx-auto w-full">
              <p className="data-label text-[#c8b273] mb-1 md:mb-2">Carp Cast Commerce</p>
              <h1 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-extrabold uppercase tracking-wider text-white">
                Coming Soon — Partner Brands &amp; Fisheries
              </h1>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-14">
          <ShopComingSoon />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="shop-page">
      {/* Hero */}
      <div 
        className="relative h-48 md:h-64 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1532724414795-8a873d116fab?w=1200')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#050605] via-[#050605]/70 to-transparent" />
        <div className="relative z-10 h-full flex items-end pb-6 md:pb-8 px-4 md:px-8">
          <div className="max-w-7xl mx-auto w-full flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <p className="data-label text-[#c8b273] mb-1 md:mb-2">Pro Shop</p>
              <h1 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-extrabold uppercase tracking-wider text-white">
                Gear Up for Success
              </h1>
            </div>
            {/* Cart button - hidden when affiliate mode is active */}
            {ENABLE_STRIPE_CHECKOUT && (
              <Button 
                className="btn-gold relative w-fit"
                onClick={() => setCartOpen(true)}
                data-testid="cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Cart (£{cartTotal.toFixed(2)})
                {cartItemCount > 0 && (
                  <span className="absolute -top-2 -right-2 w-6 h-6 bg-[#ff5e00] rounded-full flex items-center justify-center text-xs font-bold">
                    {cartItemCount}
                  </span>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8">
        <Tabs defaultValue="products" className="w-full">
          <TabsList className="bg-[#0b0d0b] border border-white/10 mb-6">
            <TabsTrigger value="products" className="data-[state=active]:bg-[#c8b273] data-[state=active]:text-black" data-testid="products-tab">
              <ShoppingBag className="w-4 h-4 mr-2" />
              Products
            </TabsTrigger>
            {user?.is_admin && (
              <TabsTrigger value="bait-subscription" className="data-[state=active]:bg-[#c8b273] data-[state=active]:text-black" data-testid="bait-subscription-tab">
                <Gift className="w-4 h-4 mr-2" />
                Bait Boxes
                <span className="ml-2 text-[10px] uppercase tracking-widest text-[#c8b273]/80 border border-[#c8b273]/40 rounded px-1.5 py-0.5">Admin Preview</span>
              </TabsTrigger>
            )}
          </TabsList>


          {/* Products Tab */}
          <TabsContent value="products">
            <div className="flex gap-8">
              {/* Sidebar - Desktop */}
              <CategorySidebar />

              {/* Main Content */}
              <div className="flex-1 min-w-0">
                {/* Search & Sort Bar */}
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                  {/* Mobile Category Toggle */}
                  <Button 
                    variant="ghost" 
                    className="lg:hidden btn-ghost"
                    onClick={() => setMobileFiltersOpen(true)}
                  >
                    <Filter className="w-4 h-4 mr-2" />
                    Categories
                  </Button>

                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4a504a]" />
                    <Input
                      placeholder="Search products..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-10 bg-[#0b0d0b] border-white/10 text-white"
                      data-testid="search-input"
                    />
                  </div>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-full sm:w-48 bg-[#0b0d0b] border-white/10 text-white">
                      <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0b0d0b] border-white/10">
                      <SelectItem value="featured">Featured</SelectItem>
                      <SelectItem value="price-low">Price: Low to High</SelectItem>
                      <SelectItem value="price-high">Price: High to Low</SelectItem>
                      <SelectItem value="name">Name A-Z</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Category Header */}
                {selectedCategory !== 'all' && (
                  <div className="mb-6 pb-4 border-b border-white/5">
                    <div className="flex items-center gap-2 text-[#8a948a] text-sm mb-2">
                      <button onClick={() => setSelectedCategory('all')} className="hover:text-[#c8b273]">
                        All Products
                      </button>
                      <ChevronRight className="w-4 h-4" />
                      <span className="text-white">{SHOP_CATEGORIES[selectedCategory]?.name}</span>
                    </div>
                    <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white">
                      {SHOP_CATEGORIES[selectedCategory]?.name}
                    </h2>
                    <p className="text-[#8a948a] mt-1">{SHOP_CATEGORIES[selectedCategory]?.description}</p>
                  </div>
                )}

                {/* Products Grid */}
                {loading ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {[1, 2, 3, 4, 5, 6].map(i => (
                      <div key={i} className="glass-card h-64 animate-pulse bg-[#141714]" />
                    ))}
                  </div>
                ) : filteredProducts.length === 0 ? (
                  <div className="glass-card p-12 text-center">
                    <ShoppingBag className="w-16 h-16 text-[#4a504a] mx-auto mb-4" />
                    <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-white mb-2">
                      No Products Found
                    </h3>
                    <p className="text-[#8a948a]">
                      {search ? 'Try a different search term' : 'Check back soon for new products!'}
                    </p>
                    {(search || selectedCategory !== 'all' || selectedBrand !== 'all') && (
                      <Button 
                        className="btn-ghost mt-4"
                        onClick={() => { setSearch(''); setSelectedCategory('all'); setSelectedBrand('all'); }}
                      >
                        Clear Filters
                      </Button>
                    )}
                  </div>
                ) : (
                  <>
                    <p className="text-[#8a948a] mb-4 text-sm">
                      Showing {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {filteredProducts.map((product, index) => (
                        <motion.div
                          key={product.product_id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.02 }}
                        >
                          <ProductCard product={product} addToCart={addToCart} />
                        </motion.div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Bait Subscription Tab — INTERNAL ADMIN PREVIEW
              Hidden from anglers while the bait fulfilment partnership and
              public checkout are still being scoped. Backend endpoints also
              gate to admin so direct API hits return 404. */}
          {user?.is_admin && (
          <TabsContent value="bait-subscription" id="bait-sub-section">
            {/* Internal preview banner */}
            <div className="mb-6 rounded border border-[#c8b273]/40 bg-[#c8b273]/5 px-4 py-3 flex items-start gap-3" data-testid="bait-internal-preview-banner">
              <Gift className="w-4 h-4 text-[#c8b273] mt-0.5 flex-shrink-0" />
              <div className="text-xs">
                <p className="font-bold uppercase tracking-widest text-[#c8b273]">Internal preview · not public</p>
                <p className="text-[#8a948a] mt-0.5">
                  Public checkout is disabled. Quantities follow UK supplier packaging norms
                  (boilies in 1kg / 2kg / 3kg). Pricing and margins are placeholders until
                  the fulfilment partner is finalised.
                </p>
              </div>
            </div>

            {/* Monthly Subscriptions */}
            <div className="text-center mb-12">
              <p className="data-label text-[#c8b273] mb-2">Monthly Delivery</p>
              <h2 className="font-['Barlow_Condensed'] text-4xl font-extrabold uppercase tracking-wider text-white mb-4">
                Bait Subscription Boxes
              </h2>
              <p className="text-[#8a948a] max-w-2xl mx-auto">
                Three tiers of monthly bait drops — from a starter top-up to a full specimen-session prep kit.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-16">
              {[
                {
                  id: 'starter',
                  name: 'Starter Box',
                  description: 'Entry-level monthly bundle. One realistic session of essentials at an approachable price.',
                  price: 24.99,
                  items: ['1kg Shelf-Life Boilies', '100g Matching Pop-ups', 'Sample hookbait or small extra'],
                },
                {
                  id: 'angler',
                  name: 'Angler Box',
                  description: 'Mid-tier monthly bundle. Built for the regular weekend angler.',
                  price: 44.99,
                  items: ['2kg Shelf-Life Boilies', '200g Matching Pop-ups', '900g Method/Stick Pellets', '250ml Matching Liquid / Glug'],
                },
                {
                  id: 'specimen',
                  name: 'Specimen Box',
                  description: 'Premium monthly bundle for serious session/specimen anglers.',
                  price: 74.99,
                  items: ['3kg Shelf-Life Boilies', '400g Pop-ups / Hookbaits', '500ml Matching Liquid / Glug', '2 × Hookbait Extras'],
                },
              ].map((box, index) => (
                <motion.div
                  key={box.id}
                  className={`glass-card p-6 relative ${box.id === 'angler' ? 'elite-badge' : ''}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`bait-box-${box.id}`}
                >
                  {box.id === 'angler' && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#c8b273] text-black text-xs font-bold uppercase tracking-widest px-4 py-1 rounded">
                      Most Popular
                    </div>
                  )}

                  <div className="text-center mb-6">
                    <Gift className={`w-12 h-12 mx-auto mb-4 ${
                      box.id === 'specimen' ? 'text-[#c8b273]' : 
                      box.id === 'angler' ? 'text-[#ff5e00]' : 
                      'text-[#6b7d6b]'
                    }`} />
                    <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wide text-white">
                      {box.name}
                    </h3>
                    <p className="text-[#8a948a] text-sm mt-2">{box.description}</p>
                  </div>

                  <div className="text-center mb-6">
                    <span className="font-['Barlow_Condensed'] text-4xl font-extrabold text-[#c8b273]">
                      £{box.price}
                    </span>
                    <span className="text-[#8a948a] ml-1">/month</span>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {box.items?.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-[#22c55e] flex-shrink-0" />
                        <span className="text-[#e2e4e2]">{item}</span>
                      </li>
                    ))}
                  </ul>

                  <Button 
                    className={box.id === 'angler' ? 'btn-gold w-full' : 'btn-ghost w-full'}
                    onClick={() => handleBaitSubscription(box.id)}
                    data-testid={`subscribe-${box.id}`}
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Subscribe Monthly
                  </Button>

                  <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-center gap-4 text-xs text-[#8a948a]">
                    <span className="flex items-center gap-1">
                      <Truck className="w-3 h-3" />
                      Free Delivery
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Seasonal & Situational Bundles - One Time Purchase */}
            <div className="border-t border-white/10 pt-12">
              <div className="text-center mb-12">
                <p className="data-label text-[#22c55e] mb-2">One-Time Purchase</p>
                <h2 className="font-['Barlow_Condensed'] text-3xl font-extrabold uppercase tracking-wider text-white mb-4">
                  Seasonal & Situational Bundles
                </h2>
                <p className="text-[#8a948a] max-w-2xl mx-auto">
                  Specially curated bait packages for specific conditions. Buy once, fish with confidence.
                </p>
              </div>

              {/* Seasonal Bundles */}
              <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wide text-[#c8b273] mb-6 text-center">
                Seasonal Bundles
              </h3>
              
              {/* Bundle Cards - New Layout with Individual Products */}
              <div className="space-y-8 max-w-5xl mx-auto mb-12">
                {[
                  { 
                    id: 'summer', 
                    name: 'High Attraction Hit & Run Bundle', 
                    icon: '☀️', 
                    priceRange: '£30 - £50',
                    description: 'Fruity, sweet flavours optimised for warm water fishing when carp are actively feeding on the surface and in the margins.',
                    products: [
                      { name: 'Mainline Cell Boilies 18mm 5kg', price: 48.60, image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=mainline+cell+boilies' },
                      { name: 'CC Moore Live System Pop-Ups', price: 7.28, image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=cc+moore+pop+ups' },
                      { name: 'Sticky Baits Krill 15mm 1kg', price: 11.73, image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=sticky+baits+krill' },
                      { name: 'Korda Kurv Shank Hooks Size 6', price: 5.51, image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=korda+kurv+shank+hooks' },
                    ]
                  },
                  { 
                    id: 'autumn', 
                    name: 'Pre-Winter Feed-Up Bundle', 
                    icon: '🍂', 
                    priceRange: '£35 - £55',
                    description: 'Nutty, spicy baits perfect for the autumn feeding frenzy as carp bulk up before winter.',
                    products: [
                      { name: 'Mainline Essential IB 18mm 1kg', price: 13.11, image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=mainline+essential+ib' },
                      { name: 'Nash Tiger Nut Particles 2.5kg', price: 8.97, image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=nash+tiger+nut+particles' },
                      { name: 'CC Moore Live System Pop-Ups', price: 7.28, image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=cc+moore+pop+ups' },
                      { name: 'Fox Edges Rig Kit', price: 22.40, image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=fox+edges+rig+kit' },
                    ]
                  },
                  { 
                    id: 'winter', 
                    name: 'Cold Water Confidence Bundle', 
                    icon: '❄️', 
                    priceRange: '£25 - £45',
                    description: 'High-attract, fishmeal-based baits designed for cold water when carp feed less frequently.',
                    products: [
                      { name: 'Sticky Baits Krill 15mm 1kg', price: 11.73, image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=sticky+baits+krill' },
                      { name: 'CC Moore Live System Pop-Ups', price: 7.28, image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=cc+moore+live+system+pop+ups' },
                      { name: 'Korda Wide Gape Hooks Size 4', price: 5.51, image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=korda+wide+gape+hooks' },
                      { name: 'ESP Leadcore Leaders 45lb', price: 8.24, image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=esp+leadcore+leaders' },
                    ]
                  },
                  { 
                    id: 'spring', 
                    name: 'Spring Wake Up Bundle', 
                    icon: '🌱', 
                    priceRange: '£30 - £50',
                    description: 'Digestible baits for fish waking up from winter. Perfect for the new season when carp start moving.',
                    products: [
                      { name: 'Mainline Cell Boilies 18mm 5kg', price: 48.60, image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=mainline+cell+boilies' },
                      { name: 'Nash Tiger Nut Particles 2.5kg', price: 8.97, image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=nash+tiger+nut+particles' },
                      { name: 'CC Moore Live System Pop-Ups', price: 7.28, image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=cc+moore+pop+ups' },
                      { name: 'Korda Kurv Shank Hooks Size 6', price: 5.51, image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=korda+kurv+shank+hooks' },
                    ]
                  },
                ].map((bundle, index) => (
                  <motion.div
                    key={bundle.id}
                    className="glass-card p-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    data-testid={`bundle-${bundle.id}`}
                  >
                    {/* Bundle Header */}
                    <div className="flex items-start gap-4 mb-4">
                      <span className="text-4xl">{bundle.icon}</span>
                      <div className="flex-1">
                        <h4 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wide text-white">
                          {bundle.name}
                        </h4>
                        <p className="text-[#22c55e] font-['Barlow_Condensed'] text-lg font-bold mt-1">
                          {bundle.priceRange}
                          <span className="text-[#6b7d6b] text-xs font-normal ml-2">estimated range</span>
                        </p>
                        <p className="text-[#8a948a] text-sm mt-2">{bundle.description}</p>
                        <p className="text-[#c8b273] text-xs mt-2 font-medium">
                          Buy only what you need — all items listed below.
                        </p>
                      </div>
                    </div>
                    
                    {/* Recommended Setup Label */}
                    <div className="border-t border-white/10 pt-4 mb-4">
                      <p className="text-[#c8b273] text-xs uppercase tracking-widest font-bold mb-4">
                        Recommended Setup for This Session
                      </p>
                    </div>
                    
                    {/* Individual Products Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {bundle.products.map((product, pIndex) => (
                        <div 
                          key={pIndex}
                          className="bg-[#141714] rounded-lg overflow-hidden group"
                        >
                          <div className="aspect-square bg-[#0b0d0b] relative overflow-hidden">
                            <img 
                              src={product.image} 
                              alt={product.name}
                              className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                            />
                          </div>
                          <div className="p-3">
                            <h5 className="text-white text-xs font-medium line-clamp-2 mb-2 min-h-[2.5rem]">
                              {product.name}
                            </h5>
                            <Button 
                              className="btn-ghost w-full text-xs py-1.5"
                              onClick={() => openExternalLink(product.url)}
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              Buy from Retailer
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <p className="text-[#6b7d6b] text-[10px] text-center mt-4">
                      You'll be redirected to a trusted UK retailer to complete your purchase
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* Situational Bundles */}
              <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wide text-[#c8b273] mb-6 text-center">
                Situational Bundles
              </h3>
              <div className="space-y-8 max-w-5xl mx-auto">
                {[
                  { 
                    id: 'overnighter', 
                    name: 'Quick Hit Overnight Kit', 
                    icon: '🌙', 
                    priceRange: '£25 - £40',
                    description: 'Compact, high-impact kit for quick overnight sessions. Maximum attraction, minimum fuss.',
                    products: [
                      { name: 'Sticky Baits Krill 15mm 1kg', price: 11.73, image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=sticky+baits+krill+boilies' },
                      { name: 'CC Moore Live System Pop-Ups', price: 7.28, image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=cc+moore+pop+ups' },
                      { name: 'Korda Kurv Shank Hooks Size 6', price: 5.51, image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=korda+kurv+shank+hooks' },
                      { name: 'Fox Edges Rig Kit', price: 22.40, image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=fox+edges+rig+kit' },
                    ]
                  },
                  { 
                    id: 'pressured', 
                    name: 'Pressured Water Edge Pack', 
                    icon: '🎯', 
                    priceRange: '£30 - £50',
                    description: 'Subtle, washed-out baits and refined presentations for educated fish on busy venues.',
                    products: [
                      { name: 'Mainline Essential IB 18mm 1kg', price: 13.11, image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=mainline+essential+ib' },
                      { name: 'CC Moore Live System Pop-Ups', price: 7.28, image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=cc+moore+pop+ups+washed+out' },
                      { name: 'ESP Leadcore Leaders 45lb', price: 8.24, image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=esp+leadcore+leaders' },
                      { name: 'Korda Wide Gape Hooks Size 4', price: 5.51, image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=korda+wide+gape+hooks' },
                    ]
                  },
                  { 
                    id: 'surface', 
                    name: 'Surface Fishing Pack', 
                    icon: '🎣', 
                    priceRange: '£20 - £35',
                    description: 'Everything for exciting surface fishing in warmer months. Target carp off the top.',
                    products: [
                      { name: 'Surface Mixers & Dog Biscuits', price: 8.99, image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=surface+mixers+dog+biscuits' },
                      { name: 'Surface Controllers', price: 12.99, image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=surface+controller+float' },
                      { name: 'Floating Hook Baits', price: 6.99, image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=floating+hook+baits+carp' },
                      { name: 'Surface Hooks & Rigs', price: 5.99, image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=300', url: 'https://www.anglingdirect.co.uk/catalogsearch/result/?q=surface+fishing+hooks' },
                    ]
                  },
                ].map((bundle, index) => (
                  <motion.div
                    key={bundle.id}
                    className="glass-card p-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    data-testid={`bundle-${bundle.id}`}
                  >
                    {/* Bundle Header */}
                    <div className="flex items-start gap-4 mb-4">
                      <span className="text-4xl">{bundle.icon}</span>
                      <div className="flex-1">
                        <h4 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wide text-white">
                          {bundle.name}
                        </h4>
                        <p className="text-[#22c55e] font-['Barlow_Condensed'] text-lg font-bold mt-1">
                          {bundle.priceRange}
                          <span className="text-[#6b7d6b] text-xs font-normal ml-2">estimated range</span>
                        </p>
                        <p className="text-[#8a948a] text-sm mt-2">{bundle.description}</p>
                        <p className="text-[#c8b273] text-xs mt-2 font-medium">
                          Buy only what you need — all items listed below.
                        </p>
                      </div>
                    </div>
                    
                    {/* Recommended Setup Label */}
                    <div className="border-t border-white/10 pt-4 mb-4">
                      <p className="text-[#c8b273] text-xs uppercase tracking-widest font-bold mb-4">
                        Recommended Setup for This Session
                      </p>
                    </div>
                    
                    {/* Individual Products Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {bundle.products.map((product, pIndex) => (
                        <div 
                          key={pIndex}
                          className="bg-[#141714] rounded-lg overflow-hidden group"
                        >
                          <div className="aspect-square bg-[#0b0d0b] relative overflow-hidden">
                            <img 
                              src={product.image} 
                              alt={product.name}
                              className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                            />
                          </div>
                          <div className="p-3">
                            <h5 className="text-white text-xs font-medium line-clamp-2 mb-2 min-h-[2.5rem]">
                              {product.name}
                            </h5>
                            <Button 
                              className="btn-ghost w-full text-xs py-1.5"
                              onClick={() => openExternalLink(product.url)}
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              Buy from Retailer
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <p className="text-[#6b7d6b] text-[10px] text-center mt-4">
                      You'll be redirected to a trusted UK retailer to complete your purchase
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Mobile Category Drawer */}
      <Dialog open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
        <DialogContent className="bg-[#0b0d0b] border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white">
              Categories
            </DialogTitle>
          </DialogHeader>
          <CategorySidebar mobile />
        </DialogContent>
      </Dialog>

      {/* Cart Drawer - only shown when Stripe checkout is enabled */}
      {ENABLE_STRIPE_CHECKOUT && (
        <Dialog open={cartOpen} onOpenChange={setCartOpen}>
          <DialogContent className="bg-[#0b0d0b] border-white/10 sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white flex items-center gap-2">
                <ShoppingCart className="w-6 h-6 text-[#c8b273]" />
                Your Cart
              </DialogTitle>
          </DialogHeader>
          
          {cart.length === 0 ? (
            <div className="py-8 text-center">
              <ShoppingBag className="w-12 h-12 text-[#4a504a] mx-auto mb-4" />
              <p className="text-[#8a948a]">Your cart is empty</p>
            </div>
          ) : (
            <>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {cart.map((item) => (
                  <div key={item.product_id} className="flex gap-4 p-3 bg-[#141714] rounded">
                    <div className="w-16 h-16 bg-[#0b0d0b] rounded flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {productHasImage(item) ? (
                        <img
                          src={resolveProductImage(item)}
                          alt={item.product_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <ProductImagePlaceholder category={item.category} compact />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-bold text-sm truncate">{item.product_name}</p>
                      <p className="text-[#6b7d6b] text-xs">{item.brand_name}</p>
                      <p className="text-[#c8b273] font-bold">£{(item.retail_price || 0).toFixed(2)}</p>
                      {(item.is_purchasable === false || item.category === 'Bait') && (
                        <p className="text-amber-400 text-[10px] uppercase tracking-wider mt-1" data-testid={`cart-disabled-${item.product_id}`}>
                          Partner launch pending — remove to checkout
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <button 
                          onClick={() => updateCartQuantity(item.product_id, -1)}
                          className="w-6 h-6 bg-[#0b0d0b] rounded flex items-center justify-center text-white hover:bg-[#2d352d]"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="text-white w-8 text-center">{item.quantity}</span>
                        <button 
                          onClick={() => updateCartQuantity(item.product_id, 1)}
                          className="w-6 h-6 bg-[#0b0d0b] rounded flex items-center justify-center text-white hover:bg-[#2d352d]"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <button 
                      onClick={() => removeFromCart(item.product_id)}
                      className="text-[#ef4444] hover:text-red-300"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="border-t border-white/10 pt-4 mt-4">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[#8a948a] uppercase text-sm tracking-wider">Total</span>
                  <span className="font-['Barlow_Condensed'] text-2xl font-bold text-[#c8b273]">
                    £{cartTotal.toFixed(2)}
                  </span>
                </div>
                <Button 
                  className="btn-gold w-full"
                  onClick={() => {
                    setCartOpen(false);
                    handleCheckout();
                  }}
                  data-testid="checkout-btn"
                >
                  Proceed to Checkout
                </Button>
                <p className="text-[#6b7d6b] text-xs text-center mt-2">
                  Free shipping on all orders
                </p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      )}
    </div>
  );
};

// Product Card Component
const ProductCard = ({ product, addToCart, compact = false }) => {
  const isOutOfStock = product.stock_status === 'out_of_stock';
  // Server-flagged sale-disabled (e.g. Bait while partnerships pending).
  // Falls back to client-side category check so older API responses still
  // gate correctly.
  const NON_PURCHASABLE_CATEGORIES = ['Bait'];
  const isNonPurchasable =
    product.is_purchasable === false ||
    NON_PURCHASABLE_CATEGORIES.includes(product.category);
  const disabledReason = product.purchase_disabled_reason
    || (product.category === 'Bait' ? 'Bait partnerships launching soon' : 'Coming soon');

  // Handle click on affiliate link - Safari compatible
  const handleBuyClick = (e) => {
    e.stopPropagation();
    e.preventDefault();

    // If the product is not currently purchasable AND has no affiliate URL,
    // surface the friendly "coming soon" message instead of opening a search.
    if (isNonPurchasable && !product.affiliate_url) {
      toast.message(disabledReason, { duration: 5000 });
      return;
    }

    const url = product.affiliate_url || `https://www.anglingdirect.co.uk/search?q=${encodeURIComponent(product.product_name)}`;

    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <div 
      className={`glass-card group overflow-hidden h-full flex flex-col ${isOutOfStock ? 'opacity-60' : ''}`}
      data-testid={`product-card-${product.product_id}`}
    >
      <div className={`${compact ? 'aspect-square' : 'aspect-[4/3]'} bg-[#141714] relative overflow-hidden`}>
        {productHasImage(product) ? (
          <img
            src={resolveProductImage(product)}
            alt={product.product_name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              // If the real upload fails to load, fall through to the placeholder
              // by clearing the src — the parent div's background gradient + the
              // overlaid placeholder card will take over.
              e.currentTarget.style.display = 'none';
              const ph = e.currentTarget.nextElementSibling;
              if (ph) ph.style.display = '';
            }}
          />
        ) : null}
        {!productHasImage(product) && (
          <ProductImagePlaceholder category={product.category} testId={`product-placeholder-${product.product_id}`} />
        )}

        {/* Badges */}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {product.brand_name && (
            <span className="bg-[#c8b273] text-black text-xs font-bold uppercase px-2 py-0.5 rounded">
              {product.brand_name.split(' ')[0]}
            </span>
          )}
          {isOutOfStock && (
            <span className="bg-red-600 text-white text-xs font-bold uppercase px-2 py-0.5 rounded">
              Out of Stock
            </span>
          )}
        </div>

        {/* Retailer Badge */}
        {product.retailer_name && (
          <div className="absolute top-2 right-2">
            <span className="bg-[#22c55e]/90 text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded">
              {product.retailer_name}
            </span>
          </div>
        )}

        {/* SKU Badge */}
        {product.sku && (
          <div className="absolute bottom-2 right-2">
            <span className="bg-black/50 text-white/70 text-[10px] font-mono px-1.5 py-0.5 rounded">
              {product.sku}
            </span>
          </div>
        )}

        {/* Coming-Soon ribbon — covers the card when bait partnerships are pending. */}
        {isNonPurchasable && (
          <div className="absolute top-2 left-2" data-testid={`partner-pending-${product.product_id}`}>
            <span className="bg-amber-500/95 text-black text-[10px] font-bold uppercase px-2 py-0.5 rounded shadow">
              Partner launch pending
            </span>
          </div>
        )}

        {/* Quick Buy Overlay - Affiliate Link */}
        {!isOutOfStock && !isNonPurchasable && (
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-4">
            <Button
              className="btn-gold"
              onClick={handleBuyClick}
              data-testid={`buy-${product.product_id}`}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Buy from Retailer
            </Button>
            <p className="text-white/70 text-[10px] text-center">
              Opens {product.retailer_name || 'trusted UK retailer'}
            </p>
          </div>
        )}
      </div>
      
      <div className="p-3 flex-1 flex flex-col">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-[#8a948a] text-xs uppercase tracking-wider">{product.category}</p>
          {product.subcategory && (
            <>
              <span className="text-[#4a504a]">•</span>
              <p className="text-[#6b7d6b] text-xs">{product.subcategory}</p>
            </>
          )}
        </div>
        <h3 className="font-['Barlow_Condensed'] text-base font-bold text-white mt-1 line-clamp-2 group-hover:text-[#c8b273] transition-colors">
          {product.product_name}
        </h3>
        {!compact && (
          <>
            <p className="text-[#6b7d6b] text-xs mt-1 line-clamp-2">{product.description}</p>
          </>
        )}
        <div className="flex items-center justify-between mt-auto pt-2">
          <p className="font-['Barlow_Condensed'] text-xl font-bold text-[#c8b273]">
            £{(product.retail_price || 0).toFixed(2)}
          </p>
          {product.default_dispatch_time && (
            <span className="text-xs text-[#6b7d6b]">{product.default_dispatch_time}</span>
          )}
        </div>
        
        {/* Mobile Buy Button */}
        {!isOutOfStock && !isNonPurchasable && (
          <div className="mt-3 lg:hidden">
            <Button
              className="btn-gold w-full text-sm"
              onClick={handleBuyClick}
              data-testid={`buy-mobile-${product.product_id}`}
            >
              <ExternalLink className="w-3 h-3 mr-1" />
              Buy from Retailer
            </Button>
            <p className="text-[#6b7d6b] text-[9px] text-center mt-1">
              You'll be redirected to a trusted UK retailer
            </p>
          </div>
        )}

        {/* Non-purchasable placeholder — replaces the buy CTA cleanly. */}
        {isNonPurchasable && (
          <div className="mt-3" data-testid={`coming-soon-block-${product.product_id}`}>
            <Button
              type="button"
              disabled
              className="w-full text-sm bg-[#1f221f] text-[#c8b273] border border-[#c8b273]/30 cursor-not-allowed"
              data-testid={`coming-soon-btn-${product.product_id}`}
            >
              <ShoppingCart className="w-3 h-3 mr-1.5" />
              Coming Soon
            </Button>
            <p className="text-[#6b7d6b] text-[10px] text-center mt-1">
              {disabledReason}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Shop;
